/* eslint-disable no-nested-ternary */
import React from "react";
import camelCase from "lodash/camelCase";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { KanbanTemplates } from "../../../model/kanban/types/KanbanTemplates.enum";
import { Rounded } from "../../../theme/Rounded";
import { Shadow } from "../../../theme/Shadow";
import { Spacing } from "../../../theme/Spacing";
import { kanbanTemplatseColumns } from "../KanbanUtils";
import { TestIDs } from "../../../tests/types/testIds";

const PickerWrapper = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.s};
`;

const CardsGrid = styled(Flex)`
  gap: ${Spacing.m};
  flex-wrap: wrap;
  flex-direction: column;
`;

const TemplateCard = styled(Button)<{ $selected: boolean }>`
  width: 100%;
  min-width: 18rem;
  margin: 0;
  padding: ${Spacing.m};
  border-radius: ${Rounded.lg};
  text-align: start;
  background-color: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.textColor};
  border: 2px solid
    ${({ $selected, theme }) => ($selected ? theme.main : theme.darkSub)};
  box-shadow: ${({ $selected }) => ($selected ? Shadow.MD : Shadow.SM)};
  transition:
    border-color 0.2s,
    box-shadow 0.2s,
    transform 0.2s;
  gap: ${Spacing.m};

  &:hover {
    transform: translateY(-1px);
    border-color: ${({ theme }) => theme.main};
    background-color: ${({ theme }) => theme.white};
  }
`;

const TemplateTitleRow = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  gap: ${Spacing.s};
  margin-bottom: ${Spacing.s};
`;

const SelectedBadge = styled(Flex)<{ $selected: boolean }>`
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.pill};
  background-color: ${({ $selected, theme }) =>
    $selected ? theme.main : theme.sub};
  color: ${({ $selected, theme }) =>
    $selected ? theme.white : theme.darkerSub};
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
`;

const MiniBoard = styled(Flex)`
  gap: ${Spacing.xs};
  overflow: hidden;
  padding: ${Spacing.s};
  border-radius: ${Rounded.md};
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.lightSub},
    ${({ theme }) => theme.sub}
  );
  border: 1px solid ${({ theme }) => theme.darkSub};
  margin-bottom: ${Spacing.s};
  align-items: stretch;
`;

const MiniColumn = styled(Flex)`
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: ${Spacing.xs};
`;

const MiniColumnHeader = styled(Flex)`
  border-radius: ${Rounded.pill};
  background-color: ${({ theme }) => theme.main};
  opacity: 0.95;
`;

const MiniColumnBody = styled(Flex)`
  flex: 1;
  min-height: 4.5rem;
  flex-direction: column;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs};
  border-radius: ${Rounded.md};
  background-color: ${({ theme }) => `${theme.white}cc`};
  border: 1px solid ${({ theme }) => theme.darkSub};
`;

const MiniCard = styled(Flex)<{ $width?: string }>`
  height: 0.75rem;
  width: ${({ $width }) => $width || "100%"};
  border-radius: ${Rounded.pill};
  background-color: ${({ theme }) => theme.white};
  border: 1px solid ${({ theme }) => theme.darkSub};
  box-shadow: ${Shadow.SM};
`;

const PillsWrapper = styled(Flex)`
  gap: ${Spacing.xs};
  flex-wrap: wrap;
`;

const ColumnPill = styled(Flex)`
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.pill};
  background-color: ${({ theme }) => theme.sub};
  color: ${({ theme }) => theme.textColor};
  font-size: 0.75rem;
  line-height: 1.1;
`;

const TemplateToImagineryCardsCountAndInfo: Record<
  KanbanTemplates,
  { count: number; info: { width: number[] } }
> = {
  [KanbanTemplates.DEFAULT]: {
    count: 6,
    info: { width: [80, 60, 77, 90, 85, 70] },
  },
  [KanbanTemplates.CUSTOMER_SUPPORT]: {
    count: 7,
    info: { width: [80, 70, 60, 75, 85, 90, 65] },
  },
  [KanbanTemplates.MARKETING]: {
    count: 7,
    info: { width: [80, 60, 90, 70, 40, 55, 65] },
  },
  [KanbanTemplates.SALES]: {
    count: 7,
    info: { width: [80, 70, 60, 75, 85, 90, 99] },
  },
};

type KanbanTemplatePickerProps = {
  value: KanbanTemplates;
  onChange: (value: KanbanTemplates) => void;
  hasClosingColumns: boolean | undefined;
};

export const KanbanTemplatePicker: React.FC<KanbanTemplatePickerProps> = ({
  value,
  onChange,
  hasClosingColumns,
}) => {
  const { t } = useTranslation();

  const templates = Object.values(KanbanTemplates);

  return (
    <PickerWrapper>
      <CardsGrid>
        {templates.map((template) => {
          const columns = kanbanTemplatseColumns[template] || [];
          const visibleColumns = columns.slice(0, -3);
          const defaultColumns = columns.slice(-3);
          const selected = value === template;

          return (
            <TemplateCard
              key={template}
              $selected={selected}
              onClick={() => onChange(template)}
              dataTestId={TestIDs.kanbans.createEdit.kanban.template(template)}
            >
              <TemplateTitleRow>
                <Text fontWeight="bold">
                  {t(`kanban.templates.${camelCase(template)}`)}
                </Text>

                <SelectedBadge $selected={selected}>
                  {selected ? t("common.selected") : t("common.choose")}
                </SelectedBadge>
              </TemplateTitleRow>

              <MiniBoard>
                {visibleColumns.map((columnName, index) => (
                  <MiniColumn key={columnName}>
                    <MiniColumnHeader justifyContent="center">
                      <Text fontSize="xs" align="center" color="white">
                        {t(
                          `kanban.templates.${camelCase(template)}.columns.${camelCase(
                            columnName,
                          )}`,
                        )}
                      </Text>
                    </MiniColumnHeader>

                    <MiniColumnBody>
                      {TemplateToImagineryCardsCountAndInfo[
                        template
                      ].info.width.map((width, cardIndex) => (
                        <MiniCard key={cardIndex} $width={`${width}%`} />
                      ))}
                    </MiniColumnBody>
                  </MiniColumn>
                ))}
              </MiniBoard>

              {hasClosingColumns ? (
                <Flex flexDirection="column" gap="xs" marginTop="m">
                  <Text fontSize="s" color="darkerSub">
                    {t(`kanban.templates.steps`)}
                  </Text>
                  <PillsWrapper>
                    {defaultColumns.map((columnName) => (
                      <ColumnPill key={columnName}>
                        {t(
                          `kanban.templates.${camelCase(template)}.columns.${camelCase(
                            columnName,
                          )}`,
                        )}
                      </ColumnPill>
                    ))}
                  </PillsWrapper>
                </Flex>
              ) : null}
            </TemplateCard>
          );
        })}
      </CardsGrid>
    </PickerWrapper>
  );
};
